import { useTranslation } from 'react-i18next';

import UnitFormService, {
  UnitFormServiceSuccess,
} from '@/components/unit/UnitFormService';
import LessonsTabContent from '@/components/lesson/LessonsTabContent';
import WorkspaceLayout from '@/components/shared/WorkspaceLayout';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Loading, Skeleton, Tabs } from '@/components/ui/daisy';
import { useTreeCourses } from '@/data/context/TreeCoursesContext';
import { useInfiniteCache } from '@/data/hook/query/v2/useCacheManager';
import useCourseTreeNavigate from '@/data/hook/course-line/useCourseTreeNavigate';
import useUnitWorkspace from '@/data/hook/unit/useUnitWorkspace';
import usePedWorkspace from '@/data/hook/ped-content/usePedWorkspace';
import { lessonBlocksQueryKeys } from '@/data/services/querykeys';
import { LessonTabs } from '@/pages/course-line/LessonWorkspace';
import { mountKey } from '@/utils/courses';
import { ROUTES, withTab } from '@/utils/routes';
import { ORDENABLE_PAGE_SIZE } from '@/constants';
import QuestionsTabContent from '@/components/activity/QuestionsTabContent';
import { useUnitContentLesson } from '@/data/hook/unit/useUnitContentLesson';
import { QuestionTypeEnum } from '@/models/Question';
import useWorkspaceTabs from '@/data/hook/useWorkspaceTabs';
import ErrorComponent from '@/components/ui/custom/ErrorComponent';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

enum UnitTabs {
  INFO = 'info',
  LESSONS = 'lessons',
  FLASHCARDS = 'flashcards',
}

const RESOURCES_TAB_VALUE = 'resources-shortcut';

export default function UnitWorkspace() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.unitForm.workspace',
  });
  const { t: tShortcut } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.lessonsTab.resourcesShortcut',
  });
  const { tree } = useTreeCourses();
  const { unit, slug, courseLineId, moduleId, isLoading } = useUnitWorkspace();
  const { coursePath } = usePedWorkspace();
  const subcategory = coursePath?.course?.module?.courseLine?.subcategory;
  const { tab, setTab } = useWorkspaceTabs<UnitTabs>({
    defaultTab: UnitTabs.INFO,
  });
  const cacheManager = useInfiniteCache(
    lessonBlocksQueryKeys.list(slug ?? '', { pageSize: ORDENABLE_PAGE_SIZE })
      ._ctx.infinity.queryKey,
  );
  const { contentLessonId, isPending, isError, retry } = useUnitContentLesson({
    enabled: tab === UnitTabs.FLASHCARDS,
  });
  const { navigate } = useCourseTreeNavigate();

  const contentLesson = unit?.contentLesson;

  const handleSuccessUpdate = (response: UnitFormServiceSuccess) => {
    cacheManager.update(response.item);
    tree?.current.update({
      id: mountKey({ type: 'unit', id: response.item.id }),
      name: response.item.name,
    });
  };

  const handleSuccessCreate = (response: UnitFormServiceSuccess) => {
    const unitKey = mountKey({ type: 'unit', id: response.item.id });
    cacheManager.add(response.item, { position: 'end' });
    tree?.current.upsert(
      {
        id: unitKey,
        name: response.item.name,
        type: 'unit',
        realId: response.item.id,
      },
      mountKey({ type: 'course', id: slug ?? '' }),
    );
    navigate(
      ROUTES.ADMIN.COURSE_LINES.UNIT(
        courseLineId,
        moduleId,
        slug ?? '',
        response.item.id,
      ),
      { treeKey: unitKey },
    );
  };

  const handleSuccess = (response: UnitFormServiceSuccess) => {
    if (response.action === 'update') handleSuccessUpdate(response);
    else handleSuccessCreate(response);
  };

  const handleOpenContentLessonResources = (lessonId: number) => {
    if (!slug) return;
    navigate(
      withTab(
        ROUTES.ADMIN.COURSE_LINES.LESSON(
          courseLineId,
          moduleId,
          slug,
          unit?.id ?? 0,
          lessonId,
        ),
        LessonTabs.RESOURCES,
      ),
      {
        treeKey: mountKey({ type: 'lesson', id: lessonId }),
      },
    );
  };

  const { isLimitedEdition } = usePedWorkspace();

  if (isLoading) return <LoadingView />;

  return (
    <WorkspaceLayout title={t('title')} subtitle={unit?.name || t('newUnit')}>
      <Tabs
        variant="box"
        className="w-fit"
        value={tab}
        onChange={value => setTab(value as UnitTabs)}
      >
        <Tabs.Tab value={UnitTabs.INFO}>{t('tabs.information')}</Tabs.Tab>
        <Tabs.Tab value={UnitTabs.LESSONS} disabled={!unit}>
          {t('tabs.lessons')}
        </Tabs.Tab>
        <Tabs.Tab value={UnitTabs.FLASHCARDS} disabled={!unit}>
          {t('tabs.flashcards')}
        </Tabs.Tab>
        {contentLesson && (
          <Tabs.Tab
            value={RESOURCES_TAB_VALUE}
            title={contentLesson.name}
            aria-label={`${tShortcut('openLabel')}: ${contentLesson.name}`}
            onClick={() => handleOpenContentLessonResources(contentLesson.id)}
          >
            {tShortcut('title')}
          </Tabs.Tab>
        )}
      </Tabs>
      <ConditionalRenderer condition={tab === UnitTabs.INFO}>
        <UnitFormService key={unit?.id} onSuccess={handleSuccess} />
      </ConditionalRenderer>
      <ConditionalRenderer condition={tab === UnitTabs.LESSONS}>
        {slug && unit?.id && (
          <LessonsTabContent
            slug={slug}
            unitId={unit.id}
            courseLineId={courseLineId}
            moduleId={moduleId}
          />
        )}
      </ConditionalRenderer>
      <ConditionalRenderer condition={tab === UnitTabs.FLASHCARDS}>
        <ConditionalRenderer
          condition={!isPending}
          fallback={
            <div className="flex items-center justify-center py-12">
              <Loading variant="spinner" size="lg" color="primary" />
            </div>
          }
        >
          <ConditionalRenderer
            condition={!isError}
            fallback={<FlashcardsErrorView onRetry={retry} />}
          >
            {slug && contentLessonId && (
              <QuestionsTabContent
                lessonId={contentLessonId}
                subcategory={subcategory}
                addBlockName="addFlashCardBlock"
                allowedTypes={[QuestionTypeEnum.FLASHCARD]}
                createQuestionBtnName="createFlashcardQuestion"
                showBlockTypeSelector={false}
                isLimitedEdition={isLimitedEdition}
              />
            )}
          </ConditionalRenderer>
        </ConditionalRenderer>
      </ConditionalRenderer>
    </WorkspaceLayout>
  );
}

const LoadingView = () => {
  return (
    <div className="flex flex-col flex-1 rounded-2xl p-6 gap-6 bg-base-100">
      <div className="flex flex-col gap-5">
        <Skeleton className="h-5 w-20" />
        <Skeleton className="h-8 w-40" />
      </div>
      <Skeleton className="h-12 w-52" />
      <Skeleton className="h-96 w-full" />
      <div className="flex justify-end">
        <Skeleton className="h-12 w-32" />
      </div>
    </div>
  );
};

const FlashcardsErrorView = ({ onRetry }: { onRetry: () => void }) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.unitForm.workspace.flashcardsError',
  });

  return (
    <div className="flex flex-col flex-1 rounded-2xl p-6 gap-6 bg-base-100">
      <ErrorComponent
        hideStatusCode
        errorTextTitle={t('title')}
        errorTextSubTitle={t('description')}
        actionButtons={[
          {
            icon: <ArrowPathIcon />,
            text: t('retryBtn'),
            action: () => onRetry(),
          },
        ]}
      />
    </div>
  );
};
